import { forwardRef, useCallback, useMemo, useState } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import { systemTemplates as storeSystemTemplates } from '$store'
import { selectedTemplate as storeSelectedTemplate } from '$store'
import * as actions from '@actions'
import { toast } from 'react-toastify'
import {
    HUMAN_STATUSES,
    DOC_CAN_BE_RESET_STATUSES,
    DOCUMENT_STATUS_UPLOADED,
    DOCUMENT_STATUS_FAILED,
    DOC_CAN_PROCESS_STATUSES,
    PURCHASE_TYPE_PROCESS_DOCUMENT,
    hasNoFile,
    ORG_DOCUMENT_STATUS_PROCESSING,
    ORG_DOCUMENT_STATUS_OCR_APPLIED,
    HUMAN_STATUSES_EXAMPLES,
    DOCUMENT_TYPES,
    DOC_NOT_READY_STATUSES, DOC_READY_STATUSES,
} from '@constants'
import { formatDate, relativeTime } from '@/utils/date'
import cn from 'clsx'

import { MdMemory, MdOutlineDelete, MdOutlineEdit, MdOutlineErrorOutline } from 'react-icons/md'
import { BsSortUp, BsSortDown, BsFillClockFill, BsBookmarkCheckFill, BsCheckLg } from 'react-icons/bs'

import './DocumentsTable.css'
import useDocumentPopupHandler from '@hook/useDocumentPopupHandler'
import usePayments from '@hook/usePayments'
import { UploadFile } from '@components/shared/UploadFile'
import ExampleManagerModal from '../exampleManager/ExampleManagerModal'

const DocumentsTable = forwardRef(({
    className,
    headers,
    orderField,
    orderDirection,
    onHeaderClick,
    isParseAdmin,
    template,
    inheritedFrom,
    currentCollection,
    // used for both delete and edit documents.
    // Users shouldn't be able to do so if its a Parse Collection unless they're Parse Admin
    canDeleteDocuments,
    collectionId,
    documents,
    markedDocuments,
    setMarkedDocuments,
    editDocument,
    setDocumentInfo,
    deleteDocument,
    getDocumentName,
    onDrop,
}, ref) => {
    
    const DOC_STATUSES = isParseAdmin && currentCollection.isExample ? HUMAN_STATUSES_EXAMPLES : HUMAN_STATUSES
    
    const [docPendingProcessingId, setDocPendingProcessingId] = useState()
    const [showCreateExample, setShowCreateExample] = useState(false)
    const [exampleDoc, setExampleDoc] = useState(null)
    
    
    const viewedDocuments = useWireValue(store.viewedDocuments)
    const runsheet = useWireValue(store.currentRunsheet)
    const remoteConfig = useWireValue(store.remoteConfig)
    const templates = useWireValue(storeSystemTemplates)
    const selectedTemplate = useWireValue(storeSelectedTemplate)
    
    const valueExtractorTemplates = useMemo(() =>
        templates.filter(st => st.system === 'value_extractor') || [],
    [templates])
    
    const selectedTemplateName = useMemo(() => {
        const template = templates.find(
            e => e.id === selectedTemplate?.value_extractor_templateId,
        )
        
        return template?.name
    }, [selectedTemplate, templates])
    
    const payments = usePayments()
    const { popupDocumentOrNavigate } = useDocumentPopupHandler()
    
    const isInTheRunsheet = useCallback(documentId => (
        runsheet?.rdj?.some(rd => rd.documentId === documentId)
    ), [runsheet])
    
    const canViewDocument = useCallback(reviewStatus => {
        
        const canViewStatuses = [
            DOCUMENT_STATUS_UPLOADED,
            ...DOC_READY_STATUSES,
        ]
        
        return canViewStatuses.includes(reviewStatus)
        
    }, [])
    
    const processDocument = async (documentId, fundingSource) => {
        
        if (!documentId && !docPendingProcessingId) return
        
        try {
            
            if (remoteConfig?.payments?.enabled)
                await actions.purchaseDocument(documentId, PURCHASE_TYPE_PROCESS_DOCUMENT, fundingSource)
            
            await actions.queueDocumentForProcessing(
                documentId || docPendingProcessingId,
                undefined,
                undefined,
                fundingSource,
            )
            
            await actions.fetchDocumentById(documentId || docPendingProcessingId)
            
            toast.success('Document queued for processing')
            
            if (remoteConfig?.payments?.enabled && !remoteConfig?.realtimeEnabled)
                await actions.updateLocalCredits()
            
            setDocPendingProcessingId()
            
        } catch (e) {
            console.error('useDocumentPopupHandler#onConfirm', e)
            toast.error('Failed to purchase document')
        }
        
    }
    
    const onProcessDocumentClick = async documentId => {
        
        setDocPendingProcessingId(documentId)
        
        if (!payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_PROCESS_DOCUMENT,
            itemCount: 1,
            onConfirmPayment:
                (remainingBalance, fundingSource) =>
                    processDocument(documentId, fundingSource),
        })) {
            await processDocument(documentId)
        }
        
    }
    
    const onOcrDocumentClick = async documentId => {
        await actions.queueDocumentForOcr(documentId)
    }
    
    const onResetDocumentStatusClick = async documentId => {
        
        try {
            await actions.resetDocumentStatus(documentId)
            await actions.fetchDocumentById(documentId)
        } catch (e) {
            // @todo handle error
            console.error('onResetDocumentStatusClick', e)
        }
        
    }
    
    const renderDropUpload = (hidden = true) => (
        <UploadFile
            accept={{ 'application/pdf': [] }}
            onDrop={onDrop}
            fieldName="document"
            hidden={hidden}
            ref={ref}
            data-test-id="CollectionDocumentsDropZone" />
    )
    
    const updateDocumentData = async (documentId, data) => {
        await actions.updateDocumentProperties(documentId, data)
    }
    
    if (!documents.length) return (
        <div className={cn('DocumentsTable')}>
            {renderDropUpload(false)}
        </div>
    )
    
    return (<>
        
        <table className={cn('DocumentsTable table table-sm table-nowrap table-pin-rows w-full', className)}>
            
            <thead>
                <tr className="sticky top-[72px]">
                    <th style={{ width: '20px' }}>
                        <input
                            type="checkbox"
                            className="checkbox-tract"
                            data-test-id="DocumentsTable-CheckAllCheckbox"
                            checked={markedDocuments.length > 0 && markedDocuments.length === documents.length}
                            onChange={() => {
                                if (markedDocuments.length > 0 && markedDocuments.length === documents.length)
                                    setMarkedDocuments([])
                                else
                                    setMarkedDocuments(documents.map(({ id }) => id))
                            }} />
                    </th>
                    {headers
                        .filter(header => header[5] ? header[5](currentCollection.isParse, isParseAdmin) : true)
                        .map(([slug, label, cls, width, sortable]) => (
                            <th className={cls}
                                style={{ width: width }}
                                key={slug}
                                onClick={sortable ? () => onHeaderClick(slug) : () => {}}>
                                <div className="flex items-center gap-2">
                                    <span>{label}</span>
                                    {sortable && orderField === slug && (
                                        orderDirection === 'DESC' ? <BsSortDown /> : <BsSortUp />
                                    )}
                                </div>
                            </th>
                        ))
                    }
                    <th style={{ width: '190px', cursor: 'default' }}>{/* Actions */}</th>
                </tr>
            </thead>
            
            <tbody>
                
                {documents.map((doc, i) => (
                    <tr key={doc.id} data-test-id={`DocumentsTableRow-${i}`}>
                        
                        <td data-test-id={`DocumentsTableRow-${i}-cell-0`}>
                            <input
                                type="checkbox"
                                className="checkbox-tract"
                                checked={markedDocuments.indexOf(doc.id) >= 0}
                                id={doc.name}
                                onChange={e => {
                                    if (e.target.checked)
                                        setMarkedDocuments([...markedDocuments, doc.id])
                                    else
                                        setMarkedDocuments(markedDocuments.filter(id => doc.id !== id))
                                }} />
                        </td>
                        <td
                            className="align-middle max-w-[10vw]"
                            data-id={doc.id}
                            data-test-id={`DocumentsTableRow-${i}-cell-1`}>
                            <div className="flex flex-col justify-center">
                                <div
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={!canViewDocument(doc.reviewStatus)
                                        ? 'Please wait until Preprocessing has completed'
                                        : ''}>
                                    <span
                                        data-tooltip-id="default-tip"
                                        data-tooltip-content={hasNoFile(doc)
                                            ? 'Index record only. No document record is available to View'
                                            : ''}
                                        className={cn(
                                            'inline-block',
                                            'max-w-full',
                                            'whitespace-nowrap',
                                            'overflow-hidden',
                                            'text-ellipsis', {
                                                'text-gray-500': hasNoFile(doc) ||
                                                    !canViewDocument(doc.reviewStatus),
                                                'cursor-pointer': !hasNoFile(doc),
                                                'pointer-events-none': !canViewDocument(doc.reviewStatus),
                                            },
                                        )}
                                        onClick={popupDocumentOrNavigate(doc)}>
                                        {doc.name}
                                    </span>
                                </div>
                                {(!!viewedDocuments[doc.id]
                                    || isInTheRunsheet(doc.id)
                                    || doc.annotationCompleted)
                                    &&
                                    <div className="flex justify-start gap-2 py-2">
                                        {!!viewedDocuments[doc.id] && (
                                            <BsFillClockFill
                                                data-tooltip-id="default-tip"
                                                data-tooltip-content={
                                                    `Viewed ${relativeTime(viewedDocuments[doc.id].viewedAt)}`} />
                                        )}
                                        {isInTheRunsheet(doc.id) && (
                                            <BsBookmarkCheckFill
                                                data-tooltip-id="default-tip"
                                                data-tooltip-content="Document present in the current Runsheet"/>
                                        )}
                                        {doc.annotationCompleted && (
                                            <BsCheckLg
                                                data-tooltip-id="default-tip"
                                                data-tooltip-content="Annotations Marked Complete"/>
                                        )}
                                    </div>}
                            </div>
                        </td>
                        
                        {!currentCollection.isParse && <td>
                            <select
                                className="select select-bordered select-xs"
                                defaultValue={doc.documentType || ''}
                                onChange={ev => updateDocumentData(doc.id,
                                    { 'documentType': ev.target.value })}>
                                
                                <option value="" disabled></option>
                                {DOCUMENT_TYPES.map(dt =>
                                    <option key={dt.id} value={dt.id}>{dt.label}</option>)}
                            </select>
                        </td>}
                        
                        {isParseAdmin && !currentCollection.isParse && <td>
                            <select
                                className="select select-bordered select-xs"
                                defaultValue={doc.processingTemplateId || ''}
                                onChange={ev => updateDocumentData(doc.id,
                                    { 'processingTemplateId': ev.target.value || null })}>
                                {(template && inheritedFrom) ?
                                    <option value="" >{`${selectedTemplateName} - 
                                        Inherited from ${inheritedFrom}`}</option>
                                    : <option value=""></option>
                                }
                                {valueExtractorTemplates.map(vet =>
                                    <option key={vet.id} value={vet.id}>{vet.name}</option>)}
                            </select>
                        </td>}
                        
                        <td className="hidden md:table-cell">
                            {formatDate(doc.createdAt, 'MMM D, YYYY')}
                        </td>
                        
                        <td>
                            <div className={cn('flex items-center content-center gap-2', {
                                'italic': (doc.reviewStatus === DOCUMENT_STATUS_UPLOADED
                                    || [ORG_DOCUMENT_STATUS_PROCESSING, ORG_DOCUMENT_STATUS_OCR_APPLIED]
                                        .includes(doc.orgStatus)),
                                'text-red-200': doc.reviewStatus === DOCUMENT_STATUS_FAILED,
                            })}>
                                <span>
                                    {DOC_STATUSES[doc.orgStatus]
                                        || doc.orgStatus
                                        || DOC_STATUSES[doc.reviewStatus]
                                        || doc.reviewStatus
                                    }
                                </span>
                                {doc.reviewStatus === DOCUMENT_STATUS_FAILED && (
                                    <MdOutlineErrorOutline className="text-base" />
                                )}
                            </div>
                        </td>
                        
                        <td>
                            
                            <div className="col-auto flex justify-end items-center content-center gap-2">
                                {isParseAdmin && currentCollection.isExample && (
                                    <>
                                        <button
                                            className="btn btn-primary btn-sm whitespace-nowrap"
                                            onClick={ async () => {
                                                const foundDoc = await actions.fetchDocumentById(doc.id)
                                                
                                                setExampleDoc(foundDoc)
                                                setShowCreateExample(true)
                                            }}>
                                                Create Example
                                        </button>
                                        {DOC_CAN_PROCESS_STATUSES.includes(doc.orgStatus || doc.reviewStatus) &&
                                            <button
                                                className="btn btn-neutral btn-sm"
                                                onClick={() => onOcrDocumentClick(doc.id)}>
                                                OCR
                                            </button>}
                                    </>
                                )}
                                {DOC_CAN_PROCESS_STATUSES.includes(doc.orgStatus || doc.reviewStatus) &&
                                    <button
                                        className={cn(
                                            'btn btn-secondary btn-sm',
                                            'mr-3 flex justify-end items-center gap-2',
                                        )}
                                        onClick={() => onProcessDocumentClick(doc.id)}
                                        data-test-id="DocumentsTableProcessButton">
                                        <MdMemory className="text-lg" />
                                        <span className="hidden md:inline">Process</span>
                                    </button>
                                }
                                
                                {isParseAdmin
                                    && DOC_CAN_BE_RESET_STATUSES.includes(doc.orgStatus || doc.reviewStatus) && (
                                    <button
                                        className="
                                            resetGlobalUploadsPanel-document-status-button mr-3 btn btn-secondary
                                            btn-sm flex justify-end items-center content-center"
                                        onClick={() => onResetDocumentStatusClick(doc.id)}
                                        disabled={DOC_NOT_READY_STATUSES.includes(doc.orgStatus || doc.reviewStatus)}>
                                        <span>Reset</span>
                                    </button>
                                )}
                                <div className="flex items-center gap-3">
                                    {canDeleteDocuments && <MdOutlineEdit
                                        data-tooltip-id="default-tip"
                                        data-tooltip-content="Edit"
                                        className="cursor-pointer"
                                        onClick={() => {
                                            editDocument(true)
                                            setDocumentInfo({
                                                id: doc.id,
                                                name: doc.name,
                                                collectionId: collectionId,
                                            })
                                        }} />}
                                    {canDeleteDocuments && <MdOutlineDelete
                                        data-tooltip-id="default-tip"
                                        data-tooltip-content="Delete"
                                        className="text-lg cursor-pointer"
                                        onClick={() => {
                                            setDocumentInfo({
                                                id: doc.id,
                                                name: doc.name,
                                            })
                                            deleteDocument(true)
                                            const docName = doc.name.split('.')
                                            
                                            docName.pop()
                                            getDocumentName(docName.join('.'))
                                        }}
                                        data-test-id={`DocumentsTableDeleteDocument-${doc.name}`} />}
                                
                                </div>
                            
                            </div>
                        </td>
                    
                    </tr>
                    
                ))}
            
            </tbody>
        
        </table>
        
        {showCreateExample && (
            <ExampleManagerModal
                show={showCreateExample}
                handleClose={() => {
                    setExampleDoc(null)
                    setShowCreateExample(false)
                }}
                document={exampleDoc}/>
        )}
        {renderDropUpload()}
    
    </>)
    
})

export default DocumentsTable
